import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react'
import { scaleLinear } from 'd3-scale'
import { fixedDecimals } from '../../../platform/fixedDecimals'
import { Money } from '../../../platform/primitives/Money'
import { useBreakpoint } from '../../../platform/responsive/useBreakpoint'
import type { LocalDate } from '../../../platform/localDate'
import type { Granularity } from '../cashflowPeriod'
import { periodLabel, periodName } from '../cashflowRules'
import type { Period } from '../cashflowWire'
import '../cashflow.css'
import { HatchPattern } from '../../../platform/charts/HatchPattern'

/**
 * The diverging income and expense chart (M3-R2, R3, R13, R19, R21).
 *
 * Income rises above a shared zero line, expense hangs below it, and the gap is
 * what was kept. Hand-rolled SVG on `d3-scale` (rule 3); every skin is a class in
 * `cashflow.css` (rules 1 and 2). The visual decisions are the approved shape
 * reports `.agent/reports/m3-shape-deficit-hatch.md` and `m3-shape-chart-scroll.md`.
 *
 * **Never compressed (M3-R13).** Bars have a minimum width per granularity and
 * breakpoint, so the plot is at least `n × pitch` wide and scrolls inside
 * `.cf-chart__viewport` when that is wider than the panel; when it is
 * narrower, the pitch stretches to fill the measured viewport. The axis column
 * is 60px (48 on a phone) or its widest label plus 12px, whichever is wider.
 * The axis and the zero line sit OUTSIDE
 * the scroller, which is how they stay put: sticky inside an overflow wrapper
 * is fragile, and outside it the edge fade cannot dim them either.
 *
 * **Deficit is four signals (M3-R3):** `--neg-fill`, a 45° hatch, a `−` net
 * label in `--neg`, and the word "deficit" in the option's name. Which periods
 * are deficits is the server's word (`deficits`, from `totals.deficit_buckets`),
 * never recomputed here; the in-progress one is outlined as partial and says
 * "so far" instead.
 *
 * **Selection is a period, by its start.** The page shows the selected period
 * itself, so there is no callout: a click or an arrow calls `onSelect` with a
 * start, the selected period is outlined and labelled in the selection
 * system's `--accent-ink` (rule 9; why no tint fill is in `cashflow.css`), and
 * the deficit hatch and sign stay on it (rule 11). A `selected` outside the
 * series selects nothing.
 *
 * **Keyboard (Review Focus 5):** a `listbox` with `aria-activedescendant` — one
 * tab stop, the arrows and Home/End move the selection, and Enter or Space
 * commits it (each reported with how, `BarPick`). A
 * listbox rather than `useRovingGrid`: that hook addresses a header-plus-rows
 * grid of cells with interactive content, and this is one row of selectable,
 * non-interactive periods, which is a listbox.
 *
 * **Money.** Heights and labels read the server's per-period figures; nothing
 * here sums. A negative magnitude (a refund larger than the spend) is drawn as
 * a zero-height bar rather than inverted, and its net label still carries the
 * server's net.
 */

/**
 * How a pick was made: a pointer (click or tap), an arrow / Home / End step, or
 * Enter / Space committing the selection where it stands. The container treats
 * them differently: a run of keys is one intent, not one per key (#720).
 */
export type BarPick = 'pointer' | 'key' | 'commit'

export type DivergingBarsProps = {
  periods: Period[]
  granularity: Granularity
  currency: string
  /** The selected period's start. */
  selected: LocalDate
  /** A pick, and how it was made. A `commit` carries the selected start itself. */
  onSelect: (start: LocalDate, via: BarPick) => void
  /** Start dates of the server's deficit periods (`deficitStarts`). A period is a deficit iff its start is here. */
  deficits: ReadonlySet<LocalDate>
  /** Id of the panel heading, which names the listbox. */
  labelledBy: string
  /**
   * One side only — a drill-down row's chart. Only that side's bars are drawn,
   * rising from a zero line at the foot of the plot and scaled to that side
   * alone, and each period is labelled with that side's server magnitude
   * instead of its net. Unset, the chart diverges as on `/cashflow`.
   */
  side?: 'income' | 'expense'
}

/**
 * The MINIMUM `[bar, gap]` in px, per granularity, phone then tablet-and-up
 * (m3-shape-chart-scroll.md). SVG geometry fed to the x positions, not a skin,
 * so it lives in TS. A bar width is not a hit target.
 *
 * A minimum, not a size: M3-R13 forbids compressing bars to fit, not widening
 * them. When the series would leave the panel part-empty the pitch stretches
 * to the measured viewport (bars capped at `MAX_BAR`); when it would not fit,
 * this pitch scrolls.
 */
type BarGap = readonly [bar: number, gap: number]
export const BAR_GEOMETRY: Record<Granularity, { phone: BarGap; wide: BarGap }> = {
  // Phone monthly is 36 + 14: a 50px column, the width "+$1,360" needs at
  // caption size. 28 + 12 drew labels wider than their column (Task 7 review).
  month: { phone: [36, 14], wide: [72, 9] },
  // Quarter uses month's pitch: the same bar-count density, so one geometry serves both.
  quarter: { phone: [36, 14], wide: [72, 9] },
  year: { phone: [44, 20], wide: [99, 9] },
}

/** Plot height, px; half of it either side of zero. */
export const PLOT_HEIGHT = 120
/** Room above and below the tallest bar for the selection outline's stroke. */
const INSET = 4
/** Outer corner radius of a bar. The corners on the zero line are square. */
const RADIUS = 6
/** The selection and partial outlines sit this far outside the data. */
const OUTLINE_GAP = 2
/** Below this pitch a per-bar net label cannot fit: "+$1,360" at caption size is ~46px. */
const LABEL_PITCH = 50
/** A stretched bar stops here; the rest of a wide column is gap. One year must not be a slab. */
export const MAX_BAR = 120
const num = (n: number) => fixedDecimals(n, 2)

/**
 * One bar as a path: the outer corners rounded, the corners on the zero line
 * square. Absolute M/L/Q commands only, every control point on the outline.
 */
function barPath(x: number, zero: number, width: number, height: number, up: boolean): string {
  const r = Math.min(RADIUS, height, width / 2)
  const s = up ? -1 : 1
  const far = zero + s * height
  const bend = far - s * r
  return [
    `M${num(x)} ${num(zero)}`,
    `L${num(x)} ${num(bend)}`,
    `Q${num(x)} ${num(far)} ${num(x + r)} ${num(far)}`,
    `L${num(x + width - r)} ${num(far)}`,
    `Q${num(x + width)} ${num(far)} ${num(x + width)} ${num(bend)}`,
    `L${num(x + width)} ${num(zero)}`,
    'Z',
  ].join(' ')
}

/** A refund can push a magnitude below zero; a bar never inverts. */
const magnitude = (cents: number) => Math.max(0, cents)

function reducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function DivergingBars({
  periods,
  granularity,
  currency,
  selected,
  onSelect,
  deficits,
  labelledBy,
  side,
}: DivergingBarsProps) {
  const isDeficit = (p: Period) => deficits.has(p.start)
  const phone = useBreakpoint() === 'phone'
  const viewport = useRef<HTMLDivElement>(null)
  // The viewport's width, measured; 0 before layout (and always in jsdom), when the minimum stands.
  const [available, setAvailable] = useState(0)
  const [minBar, minGap] = BAR_GEOMETRY[granularity][phone ? 'phone' : 'wide']
  const minPitch = minBar + minGap
  const scale = available > 0 && periods.length > 0 ? Math.max(1, available / (periods.length * minPitch)) : 1
  const pitch = minPitch * scale
  const bar = Math.min(minBar * scale, Math.max(minBar, MAX_BAR))
  const gap = pitch - bar
  const width = periods.length * pitch
  const sparse = pitch < LABEL_PITCH

  // The viewport's width is set by the panel's grid track, never by the track
  // inside it (overflow-x), so stretching cannot feed back into the measure.
  useEffect(() => {
    const el = viewport.current
    if (el === null || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => setAvailable(el.clientWidth))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const uid = useId()
  const hatchId = `cf-hatch-${uid}`
  const optionId = (i: number) => `cf-option-${uid}-${i}`
  const found = periods.findIndex((p) => p.start === selected)
  const selectedIndex = found === -1 ? null : found

  // The figure each side draws for a period: the server's own, never derived.
  const sideCents = (p: Period): number => (side === 'income' ? p.incomeCents : p.expenseCents)
  const y = useMemo(() => {
    const height = (p: Period) =>
      side === undefined ? Math.max(magnitude(p.incomeCents), magnitude(p.expenseCents)) : magnitude(side === 'income' ? p.incomeCents : p.expenseCents)
    const largest = Math.max(0, ...periods.map(height))
    // An all-zero series still needs a non-empty domain to place the zero line.
    const bottom = side === undefined ? -Math.max(1, largest) : 0
    return scaleLinear().domain([bottom, Math.max(1, largest)]).nice().range([PLOT_HEIGHT - INSET, INSET])
  }, [periods, side])
  const flat = periods.every((p) =>
    side === undefined ? magnitude(p.incomeCents) === 0 && magnitude(p.expenseCents) === 0 : magnitude(sideCents(p)) === 0,
  )
  const zero = y(0)
  const top = y.domain()[1] ?? 0
  // Nothing to scale against: only the zero, never a row of signed $0.
  const ticks = flat ? [0] : side === undefined ? [top, top / 2, 0, -top / 2, -top] : [top, top / 2, 0]

  const startSentinel = useRef<HTMLSpanElement>(null)
  const endSentinel = useRef<HTMLSpanElement>(null)
  const [more, setMore] = useState({ start: false, end: false })
  // The edge fade shows only on a side with more content. An observer on two
  // sentinels, not a scroll listener.
  useEffect(() => {
    const root = viewport.current
    const start = startSentinel.current
    const end = endSentinel.current
    if (root === null || start === null || end === null || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        setMore((prev) => {
          let next = prev
          for (const e of entries) {
            if (e.target === start) next = { ...next, start: !e.isIntersecting }
            if (e.target === end) next = { ...next, end: !e.isIntersecting }
          }
          return next
        })
      },
      { root },
    )
    observer.observe(start)
    observer.observe(end)
    return () => observer.disconnect()
  }, [])

  // The start a person just picked (click or key), so the next positioning
  // can tell a pick from a selection the page made.
  const picked = useRef<LocalDate | null>(null)
  const pick = (start: LocalDate, via: Exclude<BarPick, 'commit'>) => {
    if (start !== selected) picked.current = start
    onSelect(start, via)
  }

  // The selected period is centred. A position the chart takes on its own —
  // mount, a new selected start, a new pitch — sets the scroller's
  // `scrollLeft` and nothing else, so the page never moves; that is
  // idempotent, so StrictMode's doubled mount lands in the same place. Only a
  // person's pick scrolls the option into view, smoothly unless motion is
  // unwelcome; `block: 'nearest'` keeps the page still unless the chart is
  // off screen, where the person is already looking.
  useLayoutEffect(() => {
    const byPerson = picked.current === selected
    picked.current = null
    if (selectedIndex === null) return
    if (byPerson) {
      // jsdom implements no scrollIntoView; a real browser always does.
      document
        .getElementById(`cf-option-${uid}-${selectedIndex}`)
        ?.scrollIntoView?.({ block: 'nearest', inline: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' })
      return
    }
    const el = viewport.current
    if (el !== null) el.scrollLeft = selectedIndex * pitch + pitch / 2 - el.clientWidth / 2
  }, [selected, selectedIndex, pitch, uid])

  // Focus not obscured (WCAG 2.4.11): the ring is drawn on the selected option,
  // so a Tab into the chart after a manual scroll could put focus off-screen.
  // Keyboard focus brings that option into view, at the nearest edge. Focus a
  // pointer brought is left alone: its click makes a pick, which scrolls itself.
  const pointing = useRef(false)
  const onFocus = () => {
    const byPointer = pointing.current
    pointing.current = false
    if (byPointer || selectedIndex === null) return
    // jsdom implements no scrollIntoView; a real browser always does.
    document
      .getElementById(optionId(selectedIndex))
      ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' })
  }

  const last = periods.length - 1
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (last < 0) return
    let next: number | null = null
    const from = selectedIndex ?? last
    switch (e.key) {
      case 'ArrowRight':
        next = selectedIndex === null ? last : Math.min(last, from + 1)
        break
      case 'ArrowLeft':
        next = selectedIndex === null ? last : Math.max(0, from - 1)
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = last
        break
      case 'Enter':
      case ' ':
        // Settles the selection where it stands; with none, there is nothing to settle.
        e.preventDefault()
        if (selectedIndex !== null) onSelect(selected, 'commit')
        return
      default:
        return
    }
    e.preventDefault()
    const target = periods[next]
    if (next !== selectedIndex && target !== undefined) pick(target.start, 'key')
  }

  const tickLabel = (t: number) =>
    t === 0 ? (
      '0'
    ) : (
      // One side has no sign to tell apart: a stock figure, never "+$500".
      <Money cents={Math.round(t)} currency={currency} kind={side === undefined ? 'flow' : 'stock'} display="compact" muted />
    )

  const chartVars = { '--cf-plot-h': `${PLOT_HEIGHT}px`, '--cf-pitch': `${pitch}px` } as CSSProperties
  const plotVars = { '--cf-hatch': `url(#${hatchId})` } as CSSProperties

  return (
    <div className={`cf-chart${phone ? ' cf-chart--phone' : ''}`} style={chartVars}>
      <div className="cf-axis" aria-hidden="true">
        {ticks.map((t) => (
          <span
            key={t}
            className={t === 0 ? 'cf-axis__tick cf-axis__tick--zero' : 'cf-axis__tick'}
            style={{ top: `${num(y(t))}px` }}
          >
            {tickLabel(t)}
          </span>
        ))}
        {/* The ticks are placed absolutely and size nothing. This in-flow,
            zero-height copy gives the column its max-content width, so it grows
            to the widest label and never clips one (m3-shape-chart-scroll.md). */}
        <span className="cf-axis__sizer">
          {ticks.map((t) => (
            <span key={t} className={t === 0 ? 'cf-axis__sizer-label cf-axis__tick--zero' : 'cf-axis__sizer-label'}>
              {tickLabel(t)}
            </span>
          ))}
        </span>
      </div>
      <div
        ref={viewport}
        className={[
          'cf-chart__viewport',
          more.start ? 'cf-chart__viewport--more-start' : '',
          more.end ? 'cf-chart__viewport--more-end' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="cf-chart__track" style={{ width: `${width}px` }}>
          <span ref={startSentinel} className="cf-chart__sentinel cf-chart__sentinel--start" />
          <svg
            className="cf-plot"
            width={width}
            height={PLOT_HEIGHT}
            viewBox={`0 0 ${width} ${PLOT_HEIGHT}`}
            aria-hidden="true"
            focusable="false"
            style={plotVars}
          >
            <defs>
              <HatchPattern id={hatchId} groundClass="cf-hatch__ground" stripeClass="cf-hatch__stripe" />
            </defs>
            {periods.map((p, i) => {
              const x = i * pitch + gap / 2
              // One side rises from the foot of the plot whichever side it is; the other is not drawn.
              const incomeH = side === 'expense' ? 0 : zero - y(magnitude(p.incomeCents))
              const expenseH = side === 'income' ? 0 : side === 'expense' ? zero - y(magnitude(p.expenseCents)) : y(-magnitude(p.expenseCents)) - zero
              const deficit = isDeficit(p)
              const isSelected = i === selectedIndex
              const outline = {
                x: num(x - OUTLINE_GAP),
                y: num(zero - (side === 'expense' ? expenseH : incomeH) - OUTLINE_GAP),
                width: num(bar + 2 * OUTLINE_GAP),
                height: num(incomeH + expenseH + 2 * OUTLINE_GAP),
                rx: RADIUS + OUTLINE_GAP,
              }
              return (
                <g key={p.start} className="cf-bar-group">
                  {/* First, so the outline sits behind the data and never covers it. */}
                  {isSelected && <rect className="cf-bar--selected" {...outline} />}
                  {side !== 'expense' && <path className="cf-bar cf-bar--income" d={barPath(x, zero, bar, incomeH, true)} />}
                  {side !== 'income' && (
                    <path
                      className={deficit ? 'cf-bar cf-bar--expense cf-bar--deficit' : 'cf-bar cf-bar--expense'}
                      d={barPath(x, zero, bar, expenseH, side === 'expense')}
                    />
                  )}
                  {!isSelected && !p.complete && <rect className="cf-bar--partial" {...outline} />}
                </g>
              )
            })}
          </svg>
          <div
            className={selectedIndex === null ? 'cf-cols cf-cols--none' : 'cf-cols'}
            role="listbox"
            aria-labelledby={labelledBy}
            aria-orientation="horizontal"
            aria-activedescendant={selectedIndex === null ? undefined : optionId(selectedIndex)}
            tabIndex={0}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            onPointerDown={() => {
              pointing.current = true
            }}
            onBlur={() => {
              pointing.current = false
            }}
          >
            {periods.map((p, i) => {
              const deficit = isDeficit(p)
              return (
                <div
                  key={p.start}
                  id={optionId(i)}
                  className="cf-col"
                  role="option"
                  aria-selected={i === selectedIndex}
                  onClick={() => pick(p.start, 'pointer')}
                >
                  <span className="sr-only">
                    {periodName(p, granularity)}
                    {deficit ? ', deficit' : ''}
                    {p.complete ? '' : ', so far'}
                    {side === undefined ? ', net ' : side === 'income' ? ', received ' : ', spent '}
                    {side === undefined ? (
                      <Money cents={p.netCents} currency={currency} kind="flow" display="whole" muted />
                    ) : (
                      <Money cents={sideCents(p)} currency={currency} display="whole" muted />
                    )}
                  </span>
                  <span className="cf-label" aria-hidden="true">
                    <span className={`cf-label__period cf-label__period--${granularity}`}>{periodLabel(p, granularity)}</span>
                    {!sparse && (
                      <span className={deficit ? 'cf-label__net cf-label__net--deficit' : 'cf-label__net'}>
                        {side === undefined ? (
                          <Money cents={p.netCents} currency={currency} kind="flow" display="whole" muted />
                        ) : (
                          <Money cents={sideCents(p)} currency={currency} display="whole" muted />
                        )}
                      </span>
                    )}
                    <span className="cf-label__partial">{p.complete ? '' : 'so far'}</span>
                  </span>
                </div>
              )
            })}
          </div>
          <span ref={endSentinel} className="cf-chart__sentinel cf-chart__sentinel--end" />
        </div>
      </div>
      <div className="cf-chart__zero" aria-hidden="true" style={{ top: `${num(zero)}px` }} />
    </div>
  )
}
